export interface YAxisConfig {
  min: number;
  max: number;
  step: number;
  ticks: number[];
}

export class ChartAxisUtil {
  static calculateYAxisRange(values: (number | null)[], attributeName?: string): YAxisConfig {
    if (!values || values.length === 0) {
      return this.generateDefaultScale();
    }

    if (attributeName?.toLowerCase().includes('score')) {
      return this.generateScoreScale();
    }

    const filteredValues = values.filter((v): v is number => v !== null);
    let minValue = Math.min(...filteredValues);
    let maxValue = Math.max(...filteredValues);

    if (minValue === maxValue) {
      if (minValue === 0) {
        maxValue = 5;
      } else {
        minValue = Math.floor(minValue) - 1;
        maxValue = Math.ceil(maxValue) + 1;
      }
    }

    const range = maxValue - minValue;

    const targetTickCount = 6;
    // Permite step de 0.5 somente quando o range é pequeno E há valores com casas decimais.
    // Se todos os valores forem inteiros, o step mínimo é sempre 1.
    const hasDecimals = filteredValues.some((v) => !Number.isInteger(v));
    const minStep = range < 5 && hasDecimals ? 0.5 : 1;
    const rawStep = Math.max(range / targetTickCount, minStep);

    const step = this.calculateNiceStep(rawStep);

    const tickMin = Math.floor(minValue / step) * step;
    const tickMax = Math.ceil(maxValue / step) * step;

    const ticks: number[] = [];

    for (let i = tickMin; i <= tickMax; i += step) {
      ticks.push(Math.round(i * 100) / 100); // evita artefatos de ponto flutuante com step 0.5
    }

    return {
      min: tickMin,
      max: tickMax,
      step: step,
      ticks: ticks,
    };
  }

  private static calculateNiceStep(rawStep: number): number {
    // Permite step de 0.5 quando range < 5 (rawStep chega até 0.5 nesse caso)
    if (rawStep <= 0.5) {
      return 0.5;
    }

    if (rawStep < 1) {
      return 1;
    }

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep = rawStep / magnitude;

    let niceFraction;

    if (normalizedStep <= 1.0) {
      niceFraction = 1;
    } else if (normalizedStep <= 2.0) {
      niceFraction = 2;
    } else if (normalizedStep <= 5.0) {
      niceFraction = 5;
    } else {
      niceFraction = 10;
    }

    const calculatedStep = niceFraction * magnitude;

    return Math.max(1, calculatedStep);
  }

  private static generateDefaultScale(): YAxisConfig {
    return { min: 0, max: 100, step: 20, ticks: [0, 20, 40, 60, 80, 100] };
  }

  private static generateScoreScale(): YAxisConfig {
    return {
      min: 0,
      max: 1000,
      step: 100,
      ticks: [0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000],
    };
  }
}
